import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { STORE_ENABLED } from '../config/features';

// Language context
export interface LanguageContextType {
  language: string;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
  t: (key: string) => string;
}

const defaultLanguageContext: LanguageContextType = {
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
};

export const LanguageContext = createContext<LanguageContextType>(defaultLanguageContext);

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const translations = {
    en: {
      home: 'Home',
      about: 'About',
      companies: 'Companies',
      projects: 'Projects',
      podcasts: 'Podcasts',
      press: 'Press',
      store: 'Store',
      orders: 'My Orders',
      contact: 'Contact',
      // About dropdown items
      philosophy: 'Philosophy',
      coreValues: 'Core Values',
      journey: 'My Journey',
      // Companies dropdown items
      allCompanies: 'All Companies',
      netflipp: 'Netflipp',
      yesGroup: 'YES Group',
      bssHolding: 'BSS Holding',
      allSoftware: 'All Software 24',
      gluckspilz: 'Glückspilz',
      worldBusinessAG: 'World Business AG',
      // Podcasts dropdown items
      allPodcasts: 'All Podcasts',
      featured: 'Featured',
      latest: 'Latest Episodes',
      // Contact dropdown items
      getInTouch: 'Get in Touch',
      bookMeeting: 'Book a Meeting',
      socialMedia: 'Social Media',
      schedule: 'Schedule Meeting',
    },
    tr: {
      home: 'Ana Sayfa',
      about: 'Hakkında',
      companies: 'Şirketler',
      projects: 'Projeler',
      podcasts: 'Podcast',
      press: 'Basın',
      store: 'Mağaza',
      orders: 'Siparişlerim',
      contact: 'İletişim',
      // About dropdown items
      philosophy: 'Felsefe',
      coreValues: 'Temel Değerler',
      journey: 'Yolculuğum',
      // Companies dropdown items
      allCompanies: 'Tüm Şirketler',
      netflipp: 'Netflipp',
      yesGroup: 'YES Group',
      bssHolding: 'BSS Holding',
      allSoftware: 'All Software 24',
      gluckspilz: 'Glückspilz',
      worldBusinessAG: 'World Business AG',
      // Podcasts dropdown items
      allPodcasts: 'Tüm Podcastler',
      featured: 'Öne Çıkanlar',
      latest: 'Son Bölümler',
      // Contact dropdown items
      getInTouch: 'İletişime Geç',
      bookMeeting: 'Toplantı Ayarla',
      socialMedia: 'Sosyal Medya',
      schedule: 'Toplantı Planla',
    },
    fr: {
      home: 'Accueil',
      about: 'À Propos',
      companies: 'Entreprises',
      projects: 'Projets',
      podcasts: 'Podcasts',
      press: 'Presse',
      store: 'Boutique',
      orders: 'Mes Commandes',
      contact: 'Contact',
      // About dropdown items
      philosophy: 'Philosophie',
      coreValues: 'Valeurs Fondamentales',
      journey: 'Mon Parcours',
      // Companies dropdown items
      allCompanies: 'Toutes les Entreprises',
      netflipp: 'Netflipp',
      yesGroup: 'YES Group',
      bssHolding: 'BSS Holding',
      allSoftware: 'All Software 24',
      gluckspilz: 'Glückspilz',
      worldBusinessAG: 'World Business AG',
      // Podcasts dropdown items
      allPodcasts: 'Tous les Podcasts',
      featured: 'En Vedette',
      latest: 'Derniers Épisodes',
      // Contact dropdown items
      getInTouch: 'Prendre Contact',
      bookMeeting: 'Réserver une Réunion',
      socialMedia: 'Réseaux Sociaux',
      schedule: 'Planifier une Réunion',
    },
    es: {
      home: 'Inicio',
      about: 'Acerca de',
      companies: 'Empresas',
      projects: 'Proyectos',
      podcasts: 'Podcasts',
      press: 'Prensa',
      store: 'Tienda',
      orders: 'Mis Pedidos',
      contact: 'Contacto',
      // About dropdown items
      philosophy: 'Filosofía',
      coreValues: 'Valores Fundamentales',
      journey: 'Mi Trayectoria',
      // Companies dropdown items
      allCompanies: 'Todas las Empresas',
      netflipp: 'Netflipp',
      yesGroup: 'YES Group',
      bssHolding: 'BSS Holding',
      allSoftware: 'All Software 24',
      gluckspilz: 'Glückspilz',
      worldBusinessAG: 'World Business AG',
      // Podcasts dropdown items
      allPodcasts: 'Todos los Podcasts',
      featured: 'Destacados',
      latest: 'Últimos Episodios',
      // Contact dropdown items
      getInTouch: 'Ponerse en Contacto',
      bookMeeting: 'Reservar una Reunión',
      socialMedia: 'Redes Sociales',
      schedule: 'Programar Reunión',
    },
    ar: {
      home: 'الرئيسية',
      about: 'حول',
      companies: 'الشركات',
      projects: 'المشاريع',
      podcasts: 'البودكاست',
      press: 'الصحافة',
      store: 'المتجر',
      orders: 'طلباتي',
      contact: 'اتصل بنا',
      // About dropdown items
      philosophy: 'الفلسفة',
      coreValues: 'القيم الأساسية',
      journey: 'رحلتي',
      // Companies dropdown items
      allCompanies: 'جميع الشركات',
      netflipp: 'نتفليب',
      yesGroup: 'مجموعة YES',
      bssHolding: 'BSS القابضة',
      allSoftware: 'All Software 24',
      gluckspilz: 'Glückspilz',
      worldBusinessAG: 'World Business AG',
      // Podcasts dropdown items
      allPodcasts: 'جميع البودكاست',
      featured: 'مميز',
      latest: 'أحدث الحلقات',
      // Contact dropdown items
      getInTouch: 'تواصل معنا',
      bookMeeting: 'حجز اجتماع',
      socialMedia: 'وسائل التواصل الاجتماعي',
      schedule: 'جدولة اجتماع',
    },
  };

  const t = (key: string) => {
    return translations[language as keyof typeof translations]?.[key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Styled components
const NavbarContainer = styled.header`
  width: 100%;
  padding: 0;
  background-color: transparent;
  z-index: 1000;
  position: fixed;
  top: 0;
  height: 70px;
  display: flex;
  align-items: center;
  transition: background-color 0.3s ease;
`;

const NavContent = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  align-items: center;
  height: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    justify-content: space-between;
  }
`;

const Logo = styled(Link)`
  font-size: 1.4rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  letter-spacing: -0.02em;
  font-family: ${({ theme }) => theme.fonts.main};
  margin-right: 2rem;
  border-bottom: none;
  
  &:hover {
    color: white;
    text-decoration: none;
    border-bottom: none;
  }
  
  &::after {
    display: none;
    content: none;
    border: none;
  }
  
  &::before {
    display: none;
    content: none;
    border: none;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 1rem;
  justify-content: center;
  height: 100%;
  flex-wrap: nowrap;
  overflow: visible;
  width: 100%;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  border-bottom: none;
  white-space: nowrap;
  overflow: visible;
`;

const NavLink = styled(Link)`
  color: white;
  font-size: 0.875rem;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 1.25rem;
  cursor: pointer;
  border-bottom: none;
  white-space: nowrap;
  min-width: max-content;
  overflow: visible;
  flex-shrink: 0;
  
  &:hover {
    color: white;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 25px;
      left: 1.25rem;
      right: 1.25rem;
      height: 1px;
      background-color: white;
      transform: scaleX(1);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
  }
  
  &.active {
    color: white;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 25px;
    left: 1.25rem;
    right: 1.25rem;
    height: 1px;
    background-color: white;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
`;

const LinkWithoutDropdown = styled(Link)`
  color: white;
  font-size: 0.875rem;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 1.25rem;
  border-bottom: none;
  white-space: nowrap;
  min-width: max-content;
  overflow: visible;
  flex-shrink: 0;
  
  &:hover {
    color: white;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 25px;
      left: 1.25rem;
      right: 1.25rem;
      height: 1px;
      background-color: white;
      transform: scaleX(1);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
  }
  
  &.active {
    color: white;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 25px;
    left: 1.25rem;
    right: 1.25rem;
    height: 1px;
    background-color: white;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  padding: 0.5rem;
  z-index: 1050;
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: #333;
  font-size: 0.875rem;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.2s ease;
  border-bottom: none;
  position: relative;
  
  &:hover {
    background-color: rgba(200, 200, 200, 0.1);
    color: #333;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 1rem;
      right: 1rem;
      height: 1px;
      background-color: rgba(0, 0, 0, 0.5);
      transform: scaleX(0.5);
      transform-origin: center;
      transition: transform 0.3s ease;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: 1rem;
    right: 1rem;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.5);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.3s ease;
  }
`;

const RightSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`;

const LanguageToggle = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  text-align: center;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  text-decoration: none;
  background-color: white;
  color: black;
  border: 1px solid white;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }
  
  @media (max-width: 768px) {
    padding: 0.375rem 1rem;
    font-size: 0.75rem;
  }
`;

const CaretIcon = styled.span`
  margin-left: 4px;
  display: inline-flex;
  width: 20px;
  height: 20px;
  transform: rotate(90deg);
  
  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
`;

interface MobileMenuButtonProps {
  $isOpen: boolean;
}

const MobileMenuButton = styled.button<MobileMenuButtonProps>`
  width: 32px;
  height: 32px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  
  @media (min-width: 1025px) {
    display: none;
  }
  
  span {
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: white;
    transition: all 0.3s ease;
    
    &:first-child {
      transform: translateY(-6px);
    }
    
    &:last-child {
      transform: translateY(6px);
    }
  }
  
  ${props => props.$isOpen && `
    span {
      &:first-child {
        transform: translateY(0) rotate(45deg);
      }
      
      &:nth-child(2) {
        opacity: 0;
      }
      
      &:last-child {
        transform: translateY(0) rotate(-45deg);
      }
    }
  `}
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 900;
  padding: 2rem;
  overflow-y: auto;
  display: none;
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MobileNavLink = styled(Link)`
  color: white;
  font-size: 1.25rem;
  text-decoration: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MobileDropdownToggle = styled.div`
  color: white;
  font-size: 1.25rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MobileDropdownContent = styled(motion.div)`
  margin-top: 0.5rem;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MobileDropdownItem = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  text-decoration: none;
  padding: 0.5rem 0;
  position: relative;
  
  &:hover {
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 25%;
      right: 25%;
      width: 50%;
      height: 1px;
      background-color: rgba(255, 255, 255, 0.5);
      transform: scaleX(1);
      transition: transform 0.3s ease;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 25%;
    right: 25%;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.5);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.3s ease;
  }
`;

// Dropdown animation variants
const dropdownVariants = {
  hidden: { 
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2
    }
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

// After the NavActions styled component, add a new styled component for the language selector
const LanguageSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-left: auto;
  cursor: pointer;
  
  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const LanguageButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 8px;
  text-transform: uppercase;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const LanguageDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 4px;
  overflow: hidden;
  z-index: 1000;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const LanguageOption = styled.button`
  background: none;
  border: none;
  color: white;
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: 6px;
      left: 16px;
      right: 16px;
      height: 1px;
      background-color: white;
      transform: scaleX(1);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
  }
  
  &.active {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 6px;
    left: 16px;
    right: 16px;
    height: 1px;
    background-color: white;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
`;

// Add simple button styles
const SimpleButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 5px 10px;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.8;
  }
`;

// Navbar component
const Navbar: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/tr' || location.pathname === '/fr' || location.pathname === '/es' || location.pathname === '/ar';
  const isStorePage = location.pathname.includes('/store') || location.pathname.includes('/product/') || location.pathname.includes('/products') || 
                     location.pathname.includes('/tr/store') || location.pathname.includes('/tr/product/') || location.pathname.includes('/tr/products') || 
                     location.pathname.includes('/fr/store') || location.pathname.includes('/fr/product/') || location.pathname.includes('/fr/products') || 
                     location.pathname.includes('/es/store') || location.pathname.includes('/es/product/') || location.pathname.includes('/es/products') || 
                     location.pathname.includes('/ar/store') || location.pathname.includes('/ar/product/') || location.pathname.includes('/ar/products');
  
  // Track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  
  // Refs for dropdown items
  const aboutRef = useRef<HTMLDivElement>(null);
  const companiesRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  
  // Track which mobile dropdowns are open
  const [mobileDropdowns, setMobileDropdowns] = useState<Record<string, boolean>>({
    about: false,
    companies: false,
    contact: false
  });
  
  // Add effect to listen for video events
  const [showCloseButton, setShowCloseButton] = useState(false);
  
  // Handle scroll events to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown) {
        if (
          (openDropdown === 'about' && aboutRef.current && !aboutRef.current.contains(event.target as Node)) ||
          (openDropdown === 'companies' && companiesRef.current && !companiesRef.current.contains(event.target as Node)) ||
          (openDropdown === 'contact' && contactRef.current && !contactRef.current.contains(event.target as Node))
        ) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Toggle language dropdown
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  
  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };
  
  const changeLanguage = (lang: string) => {
    setLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  // Language display names
  const languageNames: Record<string, string> = {
    en: 'English',
    tr: 'Türkçe',
    fr: 'Français',
    es: 'Español',
    ar: 'العربية'
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdown);
    }
  };
  
  // Handle dropdown hover
  const handleDropdownHover = (dropdown: string) => {
    setOpenDropdown(dropdown);
  };
  
  // Handle dropdown leave
  const handleDropdownLeave = () => {
    setOpenDropdown(null);
  };

  // Handle section scrolling for About page sections
  const scrollToSection = (sectionId: string) => {
    // Close the dropdown
    setOpenDropdown(null);
    
    const { pathname } = window.location;
    const isOnAboutPage = pathname === '/about' || pathname.endsWith('/about') || 
                          pathname === `/${language}/about` || 
                          pathname.endsWith(`/${language}/about`);

    if (isOnAboutPage) {
      // If already on the About page, just scroll to the section
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // Fixed offsets for each section with proper typing
          // Using a smaller offset for philosophy and standard offsets for others
          const offsets: Record<string, number> = {
            'philosophy': 40,     // Reduced offset for philosophy to scroll less
            'coreValues': 80,     // Keep the same offset
            'journey': 80         // Keep the same offset
          };
          
          // Get the appropriate offset or use default
          const headerOffset = offsets[sectionId] || 80;
          
          // Get the element's position
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          
          // Smooth scroll to the element
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // If not on About page, navigation will happen automatically to the hash
      // No need for additional actions as the browser and React Router will handle it
    }
  };

  // Generate language-specific route path
  const getLocalizedPath = (path: string): string => {
    // For the home page
    if (path === '/') {
      return language === 'en' ? '/' : `/${language}`;
    }
    // For other pages
    return language === 'en' ? path : `/${language}${path}`;
  };

  // Toggle mobile dropdown
  const toggleMobileDropdown = (dropdown: string) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  // Add effect to listen for video events
  useEffect(() => {
    const videoReadyHandler = () => {
      setShowCloseButton(true);
    };
    
    // Function to handle video close
    const videoCloseHandler = () => {
      setShowCloseButton(false);
    };
    
    document.addEventListener('videoReady', videoReadyHandler);
    document.addEventListener('videoEnded', videoCloseHandler);
    
    return () => {
      document.removeEventListener('videoReady', videoReadyHandler);
      document.removeEventListener('videoEnded', videoCloseHandler);
    };
  }, []);
  
  // Function to close video
  const closeVideo = () => {
    const event = new Event('closeVideo');
    document.dispatchEvent(event);
    setShowCloseButton(false);
  };

  // Hide navbar on store pages
  if (isStorePage) {
    return null;
  }

  return (
    <NavbarContainer style={{ 
      backgroundColor: isHomePage 
        ? (isScrolled ? 'black' : 'transparent')
        : 'black',
      boxShadow: isScrolled ? '0 1px 10px rgba(0, 0, 0, 0.2)' : 'none',
    }}>
      <NavContent>
        <Logo to={getLocalizedPath('/')}>Barkın Sayın</Logo>
        <NavLinks>
          <LinkWithoutDropdown to={getLocalizedPath('/')}>
            {t('home')}
          </LinkWithoutDropdown>
          
          <NavItem 
            ref={aboutRef}
            onMouseEnter={() => handleDropdownHover('about')}
            onMouseLeave={handleDropdownLeave}
          >
            <NavLink 
              to={getLocalizedPath('/about')} 
              onClick={(e) => {
                e.stopPropagation();
                setOpenDropdown(null);
              }}
            >
              {t('about')}
              <CaretIcon>
                <svg viewBox="0 0 20 20">
                  <path d="M10.9724 10.0006L6.84766 5.87577L8.02616 4.69727L13.3295 10.0006L8.02616 15.3038L6.84766 14.1253L10.9724 10.0006Z"></path>
                </svg>
              </CaretIcon>
            </NavLink>
            
            <AnimatePresence>
              {openDropdown === 'about' && (
                <DropdownMenu
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={dropdownVariants}
                >
                  <DropdownItem 
                    to={getLocalizedPath('/about#philosophy')} 
                    onClick={() => scrollToSection('philosophy')}
                  >
                    {t('philosophy')}
                  </DropdownItem>
                  <DropdownItem 
                    to={getLocalizedPath('/about#coreValues')} 
                    onClick={() => scrollToSection('coreValues')}
                  >
                    {t('coreValues')}
                  </DropdownItem>
                  <DropdownItem 
                    to={getLocalizedPath('/about#journey')} 
                    onClick={() => scrollToSection('journey')}
                  >
                    {t('journey')}
                  </DropdownItem>
                </DropdownMenu>
              )}
            </AnimatePresence>
          </NavItem>
          
          <NavItem 
            ref={companiesRef}
            onMouseEnter={() => handleDropdownHover('companies')}
            onMouseLeave={handleDropdownLeave}
          >
            <NavLink 
              to={getLocalizedPath('/companies')} 
              onClick={(e) => {
                e.stopPropagation();
                setOpenDropdown(null);
              }}
            >
              {t('companies')}
              <CaretIcon>
                <svg viewBox="0 0 20 20">
                  <path d="M10.9724 10.0006L6.84766 5.87577L8.02616 4.69727L13.3295 10.0006L8.02616 15.3038L6.84766 14.1253L10.9724 10.0006Z"></path>
                </svg>
              </CaretIcon>
            </NavLink>
            
            <AnimatePresence>
              {openDropdown === 'companies' && (
                <DropdownMenu
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={dropdownVariants}
                >
                  <DropdownItem to={getLocalizedPath('/companies?company=all-software')} onClick={() => setOpenDropdown(null)}>
                    {t('allSoftware')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/companies?company=netflipp')} onClick={() => setOpenDropdown(null)}>
                    {t('netflipp')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/companies?company=yes-group')} onClick={() => setOpenDropdown(null)}>
                    {t('yesGroup')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/companies?company=bss-holding')} onClick={() => setOpenDropdown(null)}>
                    {t('bssHolding')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/companies?company=gluckspilz')} onClick={() => setOpenDropdown(null)}>
                    {t('gluckspilz')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/companies?company=world-business-ag')} onClick={() => setOpenDropdown(null)}>
                    {t('worldBusinessAG')}
                  </DropdownItem>
                </DropdownMenu>
              )}
            </AnimatePresence>
          </NavItem>
          
          <LinkWithoutDropdown to={getLocalizedPath('/projects')}>
            {t('projects')}
          </LinkWithoutDropdown>
          
          <LinkWithoutDropdown to={getLocalizedPath('/podcasts')}>
            {t('podcasts')}
          </LinkWithoutDropdown>
          
          <LinkWithoutDropdown to={getLocalizedPath('/press')}>
            {t('press')}
          </LinkWithoutDropdown>
          
          {STORE_ENABLED && (
            <LinkWithoutDropdown to={getLocalizedPath('/store')}>
              {t('store')}
            </LinkWithoutDropdown>
          )}
          
          <NavItem 
            ref={contactRef}
            onMouseEnter={() => handleDropdownHover('contact')}
            onMouseLeave={handleDropdownLeave}
          >
            <NavLink 
              to={getLocalizedPath('/connect')} 
              onClick={(e) => {
                e.stopPropagation();
                setOpenDropdown(null);
              }}
            >
              {t('contact')}
              <CaretIcon>
                <svg viewBox="0 0 20 20">
                  <path d="M10.9724 10.0006L6.84766 5.87577L8.02616 4.69727L13.3295 10.0006L8.02616 15.3038L6.84766 14.1253L10.9724 10.0006Z"></path>
                </svg>
              </CaretIcon>
            </NavLink>
            
            <AnimatePresence>
              {openDropdown === 'contact' && (
                <DropdownMenu
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={dropdownVariants}
                >
                  <DropdownItem to={getLocalizedPath('/schedule')} onClick={() => setOpenDropdown(null)}>
                    {t('bookMeeting')}
                  </DropdownItem>
                  <DropdownItem to={getLocalizedPath('/connect/social')} onClick={() => setOpenDropdown(null)}>
                    {t('socialMedia')}
                  </DropdownItem>
                </DropdownMenu>
              )}
            </AnimatePresence>
          </NavItem>
        </NavLinks>
        <RightSection>
          {showCloseButton && (
            <SimpleButton onClick={closeVideo} aria-label="Close video">
              ✕
            </SimpleButton>
          )}
          <LanguageSelector>
            <LanguageButton onClick={toggleLanguageDropdown}>
              {language.toUpperCase()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </LanguageButton>
            
            <AnimatePresence>
              {languageDropdownOpen && (
                <LanguageDropdown
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {Object.keys(languageNames).map((lang) => (
                    <LanguageOption
                      key={lang}
                      onClick={() => changeLanguage(lang)}
                      className={language === lang ? 'active' : ''}
                    >
                      {languageNames[lang]}
                      {language === lang && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg>
                      )}
                    </LanguageOption>
                  ))}
                </LanguageDropdown>
              )}
            </AnimatePresence>
          </LanguageSelector>
          <ActionButton to={getLocalizedPath('/connect')}>Get Started</ActionButton>
          <MobileMenuButton onClick={toggleMobileMenu} $isOpen={mobileMenuOpen}>
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuButton>
        </RightSection>
      </NavContent>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MobileMenuLinks>
              <MobileNavLink to={getLocalizedPath('/')} onClick={toggleMobileMenu}>
                {t('home')}
              </MobileNavLink>
              
              <div>
                <MobileDropdownToggle onClick={() => toggleMobileDropdown('about')}>
                  {t('about')}
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    style={{ 
                      transform: mobileDropdowns.about ? 'rotate(180deg)' : 'rotate(0)', 
                      transition: 'transform 0.3s ease' 
                    }}
                  >
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </MobileDropdownToggle>
                
                <AnimatePresence>
                  {mobileDropdowns.about && (
                    <MobileDropdownContent
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MobileDropdownItem 
                        to={getLocalizedPath('/about#philosophy')} 
                        onClick={() => {
                          scrollToSection('philosophy');
                          toggleMobileMenu();
                        }}
                      >
                        {t('philosophy')}
                      </MobileDropdownItem>
                      <MobileDropdownItem 
                        to={getLocalizedPath('/about#coreValues')} 
                        onClick={() => {
                          scrollToSection('coreValues');
                          toggleMobileMenu();
                        }}
                      >
                        {t('coreValues')}
                      </MobileDropdownItem>
                      <MobileDropdownItem 
                        to={getLocalizedPath('/about#journey')} 
                        onClick={() => {
                          scrollToSection('journey');
                          toggleMobileMenu();
                        }}
                      >
                        {t('journey')}
                      </MobileDropdownItem>
                    </MobileDropdownContent>
                  )}
                </AnimatePresence>
              </div>
              
              <div>
                <MobileDropdownToggle onClick={() => toggleMobileDropdown('companies')}>
                  {t('companies')}
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    style={{ 
                      transform: mobileDropdowns.companies ? 'rotate(180deg)' : 'rotate(0)', 
                      transition: 'transform 0.3s ease' 
                    }}
                  >
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </MobileDropdownToggle>
                
                <AnimatePresence>
                  {mobileDropdowns.companies && (
                    <MobileDropdownContent
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=all-software')} onClick={toggleMobileMenu}>
                        {t('allSoftware')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=netflipp')} onClick={toggleMobileMenu}>
                        {t('netflipp')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=yes-group')} onClick={toggleMobileMenu}>
                        {t('yesGroup')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=bss-holding')} onClick={toggleMobileMenu}>
                        {t('bssHolding')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=gluckspilz')} onClick={toggleMobileMenu}>
                        {t('gluckspilz')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/companies?company=world-business-ag')} onClick={toggleMobileMenu}>
                        {t('worldBusinessAG')}
                      </MobileDropdownItem>
                    </MobileDropdownContent>
                  )}
                </AnimatePresence>
              </div>
              
              <MobileNavLink to={getLocalizedPath('/projects')} onClick={toggleMobileMenu}>
                {t('projects')}
              </MobileNavLink>
              
              <MobileNavLink to={getLocalizedPath('/podcasts')} onClick={toggleMobileMenu}>
                {t('podcasts')}
              </MobileNavLink>
              
              <MobileNavLink to={getLocalizedPath('/press')} onClick={toggleMobileMenu}>
                {t('press')}
              </MobileNavLink>
              
              {STORE_ENABLED && (
                <MobileNavLink to={getLocalizedPath('/store')} onClick={toggleMobileMenu}>
                  {t('store')}
                </MobileNavLink>
              )}
              
              <div>
                <MobileDropdownToggle onClick={() => toggleMobileDropdown('contact')}>
                  {t('contact')}
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    style={{ 
                      transform: mobileDropdowns.contact ? 'rotate(180deg)' : 'rotate(0)', 
                      transition: 'transform 0.3s ease' 
                    }}
                  >
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </MobileDropdownToggle>
                
                <AnimatePresence>
                  {mobileDropdowns.contact && (
                    <MobileDropdownContent
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MobileDropdownItem to={getLocalizedPath('/schedule')} onClick={toggleMobileMenu}>
                        {t('bookMeeting')}
                      </MobileDropdownItem>
                      <MobileDropdownItem to={getLocalizedPath('/connect/social')} onClick={toggleMobileMenu}>
                        {t('socialMedia')}
                      </MobileDropdownItem>
                    </MobileDropdownContent>
                  )}
                </AnimatePresence>
              </div>
              
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <ActionButton to={getLocalizedPath('/connect')} onClick={toggleMobileMenu}>
                  Get Started
                </ActionButton>
              </div>
            </MobileMenuLinks>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

export default Navbar; 